import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from '../shared/api.service';
import { saveAs } from 'file-saver';
import { Common } from '../shared/common';
import { ReceivedPoll } from '../models/ReceivedPoll';
import { ParticipationResponse } from '../models/ParticipationResponse';
import { ShortVoter } from '../models/ShortVoter';
import { AssessorData } from '../models/AssessorData';
import {AuditsResponse, Checkup, CheckupComparison, ElectionEventLine} from '../models/Checkup';

@Component({
  selector: 'app-poll-audit',
  templateUrl: 'poll-audit.component.html',
  styleUrls: ['./poll-audit.component.less', '../shared/common.less']
})

export class PollAuditComponent implements OnInit {
  pollId: string;
  audits: Array<Checkup>;
  auditComparisons: Array<CheckupComparison>;
  auditEvents: Array<ElectionEventLine>;
  title: string;
  pollData: ReceivedPoll;
  votingType: string;
  startDate = '';
  endDate = '';
  votersCount = 0;
  description = '';
  votes = 0;
  percentage = 0;
  isLive: boolean;
  isPublic: boolean;
  SMSOption: boolean;
  isSending = false;
  percentageString = '0';
  isError = false;
  errorMessage: string;
  displayIndex: number;
  realIndex: number;
  shouldReturnToPoll = false;
  resultsShared = false;
  shouldDisplaySharePopup = false;
  isShareEnabled = true;
  is2FA: any;
  endDateFormat: Date;
  language = 'fr';
  hasAssessors = false;
  calculating: boolean;
  timer: any;
  assessorsValidated: boolean;
  remainingAssessorTime: string;
  shouldAssessorConvocationWarning: boolean;
  inactiveAssessors: ShortVoter[];
  remainingAssessors: number;
  displayAssessorPopup: boolean;
  assessors: number;
  isActive = false;
  displayVoterStatus = '';
  displayedVoters: ShortVoter[];
  votersTitle = '';
  auditSucceeded: boolean;

  constructor(private router: Router,
    private apiService: ApiService,
    private route: ActivatedRoute,
    public common: Common
  ) { }

  navigateArrow() {
    this.router.navigate(["/poll-ongoing", this.pollId]);
  }

  downloadDocuments() {
    this.downloadCsv();
    this.downloadVotersPdf();
    this.downloadResultsPdf();
  }

  downloadResultsPdf() {
    const name = this.getResultExcelName('pdf');
    if (this.displayIndex < 0) {
      if (this.votingType === 'list' && this.pollData.rounds.length > 1) {
        for (const round of this.pollData.rounds) {
          this.apiService.getResultsPdfRound(this.pollId, round.round, this.language).subscribe((res) => {
            const blob = new Blob([res], { type: 'application/pdf\n' });
            saveAs(blob, round.title + ' ' + name);
          });
        }
      } else {
        this.apiService.getResultsPdf(this.pollId, this.language).subscribe((res) => {
          const blob = new Blob([res], { type: 'application/pdf\n' });
          saveAs(blob, name);
        });
      }
    } else {
      this.apiService.getResultsPdfRound(this.pollId, this.realIndex, this.language).subscribe((res) => {
        const blob = new Blob([res], { type: 'application/pdf\n' });
        saveAs(blob, name);
      });
    }
  }

  downloadVotersPdf() {
    const name = this.common.getVotersExcelName('pdf', this.language);
    if (this.displayIndex < 0) {
      this.apiService.getVotersPdf(this.pollId, this.language).subscribe((res) => {
        const blob = new Blob([res], { type: 'application/pdf\n' });
        saveAs(blob, name);
      });
    } else {
      this.apiService.getVotersPdfRound(this.pollId, this.displayIndex, this.language).subscribe((res) => {
        const blob = new Blob([res], { type: 'application/pdf\n' });
        saveAs(blob, name);
      });
    }
  }

  getResultExcelName(ext: string) {
    switch (this.language) {
      case 'fr': return `Resultats.${ext}`;
      case 'es': return `Resultados.${ext}`;
      default: return `Results.${ext}`;
    }
  }

  downloadCsv() {
    const name = this.getResultExcelName('xlsx');
    if (this.displayIndex < 0) {
      if (this.votingType === 'list' && this.pollData.rounds.length > 1) {
        for (const round of this.pollData.rounds) {
          this.apiService.getResultsXlsxRound(this.pollId, round.round, this.language).subscribe((res) => {
            const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\n' });
            saveAs(blob, `${round.title} ${name}`);
          });
        }
      } else {
        this.apiService.getResultsXlsx(this.pollId, this.language).subscribe((res) => {
          const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\n' });
          saveAs(blob, name);
        });
      }
    } else {
      this.apiService.getResultsXlsxRound(this.pollId, this.realIndex, this.language).subscribe((res) => {
        const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet\n' });
        saveAs(blob, name);
      });
    }
  }

  dismissSharePopup(event: boolean) {
    if (event) {
      this.isSending = true;
      this.shouldDisplaySharePopup = false;
      this.apiService.setPublish(this.pollId).subscribe((res) => {
        this.isSending = false;
        this.resultsShared = true;
      });
    } else {
      this.shouldDisplaySharePopup = false;
    }
  }

  initData() {
    this.apiService.getPoll(this.pollId).subscribe((res: ReceivedPoll) => {
      this.apiService.getPollPreview(this.pollId).subscribe((assessors: any) => {
        this.assessorsValidated = assessors.validatedAssessors >= 2;
      });
      this.isPublic = res.publicVote;
      this.is2FA = res.is2FA;
      this.title = res.name;
      this.votersCount = res.nbVoters;
      this.description = res.description;
      this.votingType = res.type;
      this.isLive = res.isLive;
      this.SMSOption = res.smsOption;
      this.startDate = new Date(res.startDate).toLocaleString();
      if (this.displayIndex !== -1 && new Date(res.rounds[this.displayIndex].openedAt).getTime() > 0) {
        this.startDate = new Date(res.rounds[this.displayIndex].openedAt).toLocaleString();
      }
      let displayedEndDate = new Date(res.endDate);
      if (displayedEndDate != undefined) {
        const todayDate = new Date();
        if (todayDate.getTime() < displayedEndDate.getTime()) {
          this.isActive = true;
        }
      }
      if (this.displayIndex !== -1 && new Date(res.rounds[this.displayIndex].closedAt).getTime() > 0) {
        displayedEndDate = new Date(res.rounds[this.displayIndex].closedAt);
      }
      this.endDateFormat = displayedEndDate;
      this.endDate = this.endDate + displayedEndDate.toLocaleString();
      this.resultsShared = res.shareResults;
      this.hasAssessors = res.assessors > 0;
      this.assessors = res.assessors;
      this.shouldReturnToPoll = res.hasBeenClosed !== true && (new Date(res.startDate).getTime() + 3600000 * 24 > new Date().getTime());
      this.isShareEnabled = res.isLive !== true || (res.hasBeenClosed === true) || (new Date(res.endDate) < new Date());
      this.pollData = res;
      if (this.displayIndex === -1) {
        this.apiService.getParticipation(this.pollId).subscribe((partRes: ParticipationResponse) => {
          if (partRes.votes != null) {
            this.votes = partRes.votes;
          }
          this.percentage = (this.votes / this.votersCount) * 100;
          this.percentageString = parseFloat(this.percentage.toFixed(2)).toLocaleString();
        }, (err: any) => {
          this.isError = true;
          this.errorMessage = 'Nous sommes désolés mais vous ne pouvez accéder au contenu \
                erreur ' + err.status;
        });
      } else {
        this.realIndex = this.isLive && res.rounds.length >= this.displayIndex ?
          res.rounds[this.displayIndex].round : this.displayIndex;
        this.apiService.getParticipationRound(this.pollId, this.realIndex).subscribe((partRes: ParticipationResponse) => {
          if (partRes.votes != null) {
            this.votes = partRes.votes;
          }
          this.percentage = (this.votes / this.votersCount) * 100;
          this.percentageString = parseFloat(this.percentage.toFixed(2)).toLocaleString();
        }, (err: any) => {
          this.isError = true;
          this.errorMessage = 'Nous sommes désolés mais vous ne pouvez accéder au contenu \
                erreur ' + err.status;
        });
      }
    }, (err: any) => {
      this.isError = true;
      this.errorMessage = 'Nous sommes désolés mais vous ne pouvez accéder au contenu \
           erreur ' + err.status;
    });
  }

  getAudits() {
    this.apiService.getAudits(this.pollId).subscribe((res: AuditsResponse) => {
      this.audits = res.audits;
      if ( res.audits.length === 1) {
        this.getCheckupComparisons(res.audits[0], res[ res.audits.length - 1], res.checkupData);
      } else if ( res.audits.length >= 2) {
        this.getCheckupComparisons(res.audits[0], res.audits[1], res.checkupData);
      }
    });
    this.apiService.getLogs(this.pollId).subscribe((res: Array<ElectionEventLine>) => {
      this.auditEvents = res;
    });
  }

  startAudit() {
    this.apiService.startAuditOrganizer(this.pollId).subscribe((res) => {
      this.getAudits();
      this.auditSucceeded = true;
    }, (err) => {
      //console.error('Erreur lors du démarrage du contrôle :', err);
    });
  }

  downloadHistory() {
    this.apiService.getAuditHistoryPdf(this.pollId).subscribe((res) => {
      const blob = new Blob([res], { type: 'application/pdf\n' });
      saveAs(blob, 'controles.pdf');
    });
  }

  downloadLogs() {
    this.apiService.getLogsPdf(this.pollId).subscribe((res) => {
      const blob = new Blob([res], { type: 'application/pdf\n' });
      saveAs(blob, 'logs.pdf');
    });
  }

  downloadAuditDetails() {
    this.apiService.getAuditDetailsPdf(this.pollId).subscribe((res) => {
      const blob = new Blob([res], { type: 'application/pdf\n' });
      saveAs(blob, 'audits.pdf');
    });
  }

  getCheckupComparisons(currentCheckup: Checkup, initialCheckup: Checkup, checkData: Array<boolean>) {
    this.auditComparisons = [
      new CheckupComparison('Hash paramètres de l\'élection', currentCheckup.time, currentCheckup.electionHash, initialCheckup.electionHash, checkData[0]),
      new CheckupComparison('Hash version applicative', currentCheckup.time, currentCheckup.apiVersion, initialCheckup.apiVersion, checkData[1]),
      new CheckupComparison('Hash liste des candidats', currentCheckup.time, currentCheckup.questionsHash, initialCheckup.questionsHash, checkData[2]),
      new CheckupComparison('Hash liste des électeurs', currentCheckup.time, currentCheckup.votersHash, initialCheckup.votersHash, checkData[3]),
      new CheckupComparison('Empreinte liste d\'émargement', currentCheckup.time, currentCheckup.votedHash, initialCheckup.votedHash, checkData[4]),
      new CheckupComparison('Empreinte urne', currentCheckup.time, currentCheckup.tallyHash, initialCheckup.tallyHash, checkData[5]),
      new CheckupComparison('Cohérence urne/émargement', currentCheckup.time, currentCheckup.consistency, initialCheckup.consistency, checkData[6]),
      new CheckupComparison('Hash résultats', currentCheckup.time, currentCheckup.resultHash, initialCheckup.resultHash, true),
      new CheckupComparison('Hash du journal des événements', currentCheckup.time, currentCheckup.logsHash, initialCheckup.logsHash, checkData[7])
    ];
  }

  ngOnInit() {
    this.route.params
      .subscribe(params => {

        this.pollId = params['id'];
        if (params.resolution == null) {
          this.displayIndex = -1;
        } else {
          this.displayIndex = parseInt(params.resolution, 10);
        }
        this.realIndex = this.displayIndex;
        this.getAudits();
        this.initData();
      });
    const languageKey = 'userLanguage';
    const userLang = navigator.language || navigator[languageKey];
    this.language = userLang.substring(0, 2);
  }
}
